import React, { useState, useEffect } from 'react';

/**
 * Keyboard Shortcuts Help Modal
 * Shows available keyboard shortcuts when user presses Alt+?
 */
const KeyboardShortcutsHelp: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleShowHelp = () => {
      setIsVisible(true);
    };

    const handleCloseModals = () => {
      setIsVisible(false);
    };

    window.addEventListener('show-keyboard-shortcuts-help', handleShowHelp);
    window.addEventListener('close-modals', handleCloseModals);

    return () => {
      window.removeEventListener('show-keyboard-shortcuts-help', handleShowHelp);
      window.removeEventListener('close-modals', handleCloseModals);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-shortcuts-title"
      style={styles.backdrop}
      onClick={() => setIsVisible(false)}
    >
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h2 id="keyboard-shortcuts-title" style={styles.title}>
            ⌨️ Keyboard Shortcuts
          </h2>
          <button
            onClick={() => setIsVisible(false)}
            aria-label="Close keyboard shortcuts help"
            style={styles.closeButton}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          <p style={styles.description}>
            Use these keyboard shortcuts to navigate faster throughout the app.
          </p>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Navigation</h3>
            <div style={styles.shortcutList}>
              <ShortcutItem keys={['Alt', 'A']} description="Navigate to Appointments" />
              <ShortcutItem keys={['Alt', 'M']} description="Navigate to Medications/Prescriptions" />
              <ShortcutItem keys={['Alt', 'H']} description="Open Hope AI Assistant" />
              <ShortcutItem keys={['Alt', 'C']} description="Navigate to Clients (Provider only)" />
              <ShortcutItem keys={['Alt', 'S']} description="Navigate to Settings (Provider only)" />
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>General</h3>
            <div style={styles.shortcutList}>
              <ShortcutItem keys={['/']} description="Focus search field" />
              <ShortcutItem keys={['Esc']} description="Close modal or dialog" />
              <ShortcutItem keys={['Tab']} description="Navigate forward" />
              <ShortcutItem keys={['Shift', 'Tab']} description="Navigate backward" />
              <ShortcutItem keys={['Enter']} description="Activate focused element" />
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Help</h3>
            <div style={styles.shortcutList}>
              <ShortcutItem keys={['Alt', '?']} description="Show this help dialog" />
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              💡 Tip: You can disable keyboard shortcuts in{' '}
              <strong>Settings → Accessibility → Keyboard Shortcuts</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ShortcutItemProps {
  keys: string[];
  description: string;
}

const ShortcutItem: React.FC<ShortcutItemProps> = ({ keys, description }) => {
  return (
    <div style={styles.shortcutItem}>
      <div style={styles.keysContainer}>
        {keys.map((key, index) => (
          <React.Fragment key={index}>
            <kbd style={styles.key}>{key}</kbd>
            {index < keys.length - 1 && <span style={styles.plus}>+</span>}
          </React.Fragment>
        ))}
      </div>
      <span style={styles.shortcutDescription}>{description}</span>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#1A4D6F',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid #2A5470',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #2A5470'
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    color: '#FFFFFF'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '32px',
    cursor: 'pointer',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  },
  content: {
    padding: '24px'
  },
  description: {
    margin: '0 0 24px 0',
    color: '#B0C4DE',
    fontSize: '14px'
  },
  section: {
    marginBottom: '24px'
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: '#87CEEB'
  },
  shortcutList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  shortcutItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 12px',
    backgroundColor: '#0F3554',
    borderRadius: '6px'
  },
  keysContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: '140px'
  },
  key: {
    backgroundColor: '#2A5470',
    color: '#FFFFFF',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid #3A6480',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    fontFamily: 'monospace'
  },
  plus: {
    color: '#87CEEB',
    fontSize: '12px',
    fontWeight: 600,
    padding: '0 2px'
  },
  shortcutDescription: {
    color: '#FFFFFF',
    fontSize: '14px',
    flex: 1
  },
  footer: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: 'rgba(30, 144, 255, 0.1)',
    borderRadius: '6px',
    border: '1px solid rgba(30, 144, 255, 0.2)'
  },
  footerText: {
    margin: 0,
    color: '#87CEEB',
    fontSize: '13px',
    lineHeight: '1.5'
  }
};

export default KeyboardShortcutsHelp;
